// CLE-2437 — curated rendering-taxonomy options for the EDI-upload per-payer taxonomy include-filter.
//
// Covers the 43 taxonomy codes observed on Heritage (BCBS-TN) remits plus common specialty
// taxonomies, so an account can pick its per-payer "include" list in the upload dialog. This is a
// display convenience only — the include lists themselves are user-entered config, never hardcoded
// business logic. The backend filter (claimIncludedByTaxonomyFilter) matches on the raw code, so any
// code not in this list is still a valid value if it ever needs to be added here.

export interface IRenderingTaxonomyOption {
  code: string;
  label: string;
}

export const RENDERING_TAXONOMY_OPTIONS: IRenderingTaxonomyOption[] = [
  { code: "207R00000X", label: "Internal Medicine" },
  { code: "208000000X", label: "Pediatrics" },
  { code: "207Q00000X", label: "Family Medicine" },
  { code: "207V00000X", label: "OB/GYN" },
  { code: "207VF0040X", label: "OB/GYN — FPMRS" },
  { code: "363LF0000X", label: "Nurse Practitioner — Family" },
  { code: "363A00000X", label: "Physician Assistant" },
  { code: "363AM0700X", label: "Physician Assistant — Medical" },
  { code: "363L00000X", label: "Nurse Practitioner" },
  { code: "363LP0200X", label: "Nurse Practitioner — Pediatrics" },
  { code: "363LA2100X", label: "Nurse Practitioner — Acute Care" },
  { code: "363LW0102X", label: "Nurse Practitioner — Women's Health" },
  { code: "2080P0206X", label: "Pediatric Subspecialty" },
  { code: "207N00000X", label: "Dermatology" },
  { code: "207ND0101X", label: "Dermatology — MOHS Surgery" },
  { code: "207K00000X", label: "Allergy & Immunology" },
  { code: "207RE0101X", label: "Endocrinology" },
  { code: "207RR0500X", label: "Rheumatology" },
  { code: "207W00000X", label: "Ophthalmology" },
  { code: "207RG0100X", label: "Gastroenterology" },
  { code: "207Y00000X", label: "Otolaryngology (ENT)" },
  { code: "2084N0400X", label: "Neurology" },
  { code: "2084P0800X", label: "Psychiatry" },
  { code: "208600000X", label: "Surgery" },
  { code: "231H00000X", label: "Audiology" },
  { code: "103T00000X", label: "Psychologist" },
  { code: "1041C0700X", label: "Clinical Social Worker" },
  { code: "261QE0800X", label: "Clinic — Endoscopy" },
  { code: "193200000X", label: "Multi-Specialty Group" },
  // CLE-2552 — codes present on live remits but absent from the original curated set, so they
  // could not be picked in either the claims-list filter or the upload include-list. Labels are
  // the NUCC descriptions returned by NPPES, reworded to the "Parent — Specialisation" style above.
  { code: "207KA0200X", label: "Allergy & Immunology — Allergy" },
  { code: "261QA1903X", label: "Clinic — Ambulatory Surgical" },
  { code: "207X00000X", label: "Orthopaedic Surgery" },
  { code: "207XS0114X", label: "Orthopaedic Surgery — Adult Reconstructive" },
  { code: "207XS0117X", label: "Orthopaedic Surgery — Spine" },
  { code: "207XX0004X", label: "Orthopaedic Surgery — Foot & Ankle" },
  { code: "207VG0400X", label: "OB/GYN — Gynecology" },
  { code: "208200000X", label: "Plastic Surgery" },
  { code: "282N00000X", label: "General Acute Care Hospital" },
  { code: "367500000X", label: "Nurse Anesthetist (CRNA)" },
  { code: "152W00000X", label: "Optometrist" },
  { code: "122300000X", label: "Dentist" },
  { code: "1223G0001X", label: "Dentist — General Practice" },
  { code: "1223P0221X", label: "Dentist — Pediatric Dentistry" },
];

// CLE-2489 / CLE-2565 follow-up: options for the claims-list Rendering Taxonomy filter.
// Derived from the curated list above; values are the raw taxonomy code (matches
// Claim.renderingTaxonomy).
//
// Ani asked for the filter to read alphabetically. The specialty name is the meaningful key
// here (the NPI taxonomy codes are opaque), so we lead the label with the specialty name and
// sort by the composed display label — i.e. exactly the text the user reads is in locale order.
// (Sorting the raw specialty instead would drift from the displayed order in prefix cases like
// "Dentist" vs "Dentist — General Practice" because collation weights punctuation specially.)
// The `value` stays the raw code, kept visible in parentheses for reference.
export const RENDERING_TAXONOMY_FILTER_OPTIONS: {
  label: string;
  value: string;
}[] = RENDERING_TAXONOMY_OPTIONS.map((option) => ({
  label: `${option.label} (${option.code})`,
  value: option.code,
})).sort((a, b) => a.label.localeCompare(b.label));
